import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import RouletteWheel from './components/RouletteWheel';
import { calculateSpinParams } from './components/RouletteWheel';
import QROverlay from './components/QROverlay';
import PlayerList from './components/PlayerList';
import CheckinModal from './components/CheckinModal';
import CheckoutModal from './components/CheckoutModal';
import { useRoom } from './hooks/useRoom';
import './App.css';

function ResendQRModal({ name, sessionUrl, pin, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">SESSION WIEDERHERSTELLEN</h3>
        <p className="checkin-player-name">{name}</p>
        <div className="checkin-qr-wrapper">
          <QRCodeSVG value={sessionUrl} size={220} bgColor="#1a1a0a" fgColor="#d4a535" level="M" />
        </div>
        <p className="checkin-qr-hint">QR-Code auf dem Gerät des Spielers scannen</p>
        {pin && (
          <div className="checkin-pin-box">
            <span className="checkin-pin-label">Kein Kamera? PIN eingeben:</span>
            <span className="checkin-pin-code">{pin.slice(0,3)} {pin.slice(3)}</span>
          </div>
        )}
        <div className="checkin-actions">
          <button className="modal-btn-primary" onClick={onClose}>FERTIG</button>
        </div>
      </div>
    </div>
  );
}

const RED_NUMBERS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);

function getNumberColor(n) {
  if (n === 0) return 'green';
  return RED_NUMBERS.has(n) ? 'red' : 'black';
}

function getColorHex(n) {
  if (n === 0) return '#1B5E20';
  return RED_NUMBERS.has(n) ? '#C62828' : '#1B1B1B';
}

// Casino chip colors per min-bet value
const CHIP_STYLES = {
  1:   { bg: 'radial-gradient(circle at 38% 32%, #FFFFFF, #D0D0D0)', border: '#AAA',    color: '#222' },
  5:   { bg: 'radial-gradient(circle at 38% 32%, #EF5350, #B71C1C)', border: '#C62828', color: '#FFF' },
  10:  { bg: 'radial-gradient(circle at 38% 32%, #66BB6A, #1B5E20)', border: '#2E7D32', color: '#FFF' },
  25:  { bg: 'radial-gradient(circle at 38% 32%, #42A5F5, #1565C0)', border: '#1976D2', color: '#FFF' },
  100: { bg: 'radial-gradient(circle at 38% 32%, #555555, #111111)', border: '#444',    color: '#FFF' },
};

// European roulette theoretical odds
const ODDS = {
  red:    { chance: 18/37, payout: '1:1' },
  black:  { chance: 18/37, payout: '1:1' },
  zero:   { chance: 1/37,  payout: '35:1' },
  even:   { chance: 18/37, payout: '1:1' },
  odd:    { chance: 18/37, payout: '1:1' },
  low:    { chance: 18/37, payout: '1:1' },
  high:   { chance: 18/37, payout: '1:1' },
  doz1:   { chance: 12/37, payout: '2:1' },
  doz2:   { chance: 12/37, payout: '2:1' },
  doz3:   { chance: 12/37, payout: '2:1' },
  col1:   { chance: 12/37, payout: '2:1' },
  col2:   { chance: 12/37, payout: '2:1' },
  col3:   { chance: 12/37, payout: '2:1' },
  single:       { chance: 1/37,  payout: '35:1' },
  cheval:       { chance: 2/37,  payout: '17:1' },
  transversale: { chance: 3/37,  payout: '11:1' },
  carre:        { chance: 4/37,  payout: '8:1' },
  sixline:      { chance: 6/37,  payout: '5:1' },
};

function computeStats(history) {
  const total = history.length;
  if (total === 0) return null;
  const red = history.filter(n => RED_NUMBERS.has(n)).length;
  const black = history.filter(n => n !== 0 && !RED_NUMBERS.has(n)).length;
  const zero = history.filter(n => n === 0).length;
  const odd = history.filter(n => n !== 0 && n % 2 === 1).length;
  const even = history.filter(n => n !== 0 && n % 2 === 0).length;
  const low = history.filter(n => n >= 1 && n <= 18).length;
  const high = history.filter(n => n >= 19 && n <= 36).length;
  const doz1 = history.filter(n => n >= 1 && n <= 12).length;
  const doz2 = history.filter(n => n >= 13 && n <= 24).length;
  const doz3 = history.filter(n => n >= 25 && n <= 36).length;
  const col1 = history.filter(n => n !== 0 && n % 3 === 1).length;
  const col2 = history.filter(n => n !== 0 && n % 3 === 2).length;
  const col3 = history.filter(n => n !== 0 && n % 3 === 0).length;
  return { total, red, black, zero, odd, even, low, high, doz1, doz2, doz3, col1, col2, col3 };
}

const LEVEL_VALUE = { simple: 0, medium: 1, expert: 2 };

const BET_ROWS = [
  { key: 'red', level: 'simple', dot: 'red', label: 'Rot' },
  { key: 'black', level: 'simple', dot: 'black', label: 'Schwarz' },
  { key: 'zero', level: 'simple', dot: 'green', label: 'Zero' },
  { key: 'odd', level: 'simple', label: 'Ungerade' },
  { key: 'even', level: 'simple', label: 'Gerade' },
  { key: 'low', level: 'simple', label: '1 - 18' },
  { key: 'high', level: 'simple', label: '19 - 36' },
  { key: 'doz1', level: 'medium', label: '1st 12' },
  { key: 'doz2', level: 'medium', label: '2nd 12' },
  { key: 'doz3', level: 'medium', label: '3rd 12' },
  { key: 'col1', level: 'medium', label: 'Col 1' },
  { key: 'col2', level: 'medium', label: 'Col 2' },
  { key: 'col3', level: 'medium', label: 'Col 3' },
  { key: 'cheval', level: 'expert', label: '2er' },
  { key: 'transversale', level: 'expert', label: '3er Reihe' },
  { key: 'carre', level: 'expert', label: '4er Ecke' },
  { key: 'sixline', level: 'expert', label: '6er Reihe' },
  { key: 'single', level: 'simple', label: 'Plein' },
];

const BET_EXAMPLES = {
  red:          { numbers: [1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36], desc: 'Alle roten Zahlen' },
  black:        { numbers: [2,4,6,8,10,11,13,15,17,20,22,24,26,28,29,31,33,35], desc: 'Alle schwarzen Zahlen' },
  zero:         { numbers: [0], desc: 'Nur die Null' },
  odd:          { numbers: [1,3,5,7,9,11,13,15,17,19,21,23,25,27,29,31,33,35], desc: 'Alle ungeraden Zahlen' },
  even:         { numbers: [2,4,6,8,10,12,14,16,18,20,22,24,26,28,30,32,34,36], desc: 'Alle geraden Zahlen' },
  low:          { numbers: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18], desc: 'Zahlen 1 bis 18' },
  high:         { numbers: [19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36], desc: 'Zahlen 19 bis 36' },
  doz1:         { numbers: [1,2,3,4,5,6,7,8,9,10,11,12], desc: 'Erstes Dutzend' },
  doz2:         { numbers: [13,14,15,16,17,18,19,20,21,22,23,24], desc: 'Zweites Dutzend' },
  doz3:         { numbers: [25,26,27,28,29,30,31,32,33,34,35,36], desc: 'Drittes Dutzend' },
  col1:         { numbers: [1,4,7,10,13,16,19,22,25,28,31,34], desc: 'Erste Spalte' },
  col2:         { numbers: [2,5,8,11,14,17,20,23,26,29,32,35], desc: 'Zweite Spalte' },
  col3:         { numbers: [3,6,9,12,15,18,21,24,27,30,33,36], desc: 'Dritte Spalte' },
  cheval:       { numbers: [17,18], desc: 'z.B. 17 & 18 — Chip auf die Linie' },
  transversale: { numbers: [7,8,9], desc: 'z.B. 7, 8, 9 — eine Querreihe' },
  carre:        { numbers: [8,9,11,12], desc: 'z.B. 8, 9, 11, 12 — Chip auf die Ecke' },
  sixline:      { numbers: [7,8,9,10,11,12], desc: 'z.B. 7 bis 12 — zwei Querreihen' },
  single:       { numbers: [17], desc: 'z.B. nur die 17 — Chip auf die Zahl' },
};

export default function App() {
  const [spinning, setSpinning] = useState(false);
  const [targetNumber, setTargetNumber] = useState(null);
  const [winningNumber, setWinningNumber] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [history, setHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [ruleLevel, setRuleLevel] = useState('medium');
  const [minBet, setMinBet] = useState(5);
  const [selectedBet, setSelectedBet] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkoutPlayer, setCheckoutPlayer] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [resendQRPlayer, setResendQRPlayer] = useState(null); // { name, sessionUrl }
  const resultTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);
  const stats = useMemo(() => computeStats(history), [history]);
  const [currentSpinParams, setCurrentSpinParams] = useState(null);
  const { room, state: mpState, connectionStatus, notifications, createRoom, reportSpinStart, reportSpin, startNewRound, closeBets, closeRoom, playerUrl, checkin, checkout, kickPlayer, updateSettings } = useRoom();
  const isMultiplayer = !!room;

  // Betting timer countdown
  const [timerSeconds, setTimerSeconds] = useState(null);
  useEffect(() => {
    if (!mpState?.bettingDeadline) { setTimerSeconds(null); return; }
    const tick = () => {
      const s = Math.max(0, Math.round((mpState.bettingDeadline - Date.now()) / 1000));
      setTimerSeconds(s);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [mpState?.bettingDeadline]);

  const handleSpin = useCallback(() => {
    if (spinning) return;
    const target = Math.floor(Math.random() * 37);
    const params = calculateSpinParams(target);

    if (isMultiplayer && params) {
      // Send spin params to server BEFORE starting animation
      reportSpinStart(params);
    }

    setTargetNumber(target);
    setCurrentSpinParams(params);
    setSpinning(true);
    setShowResult(false);
    setWinningNumber(null);
    if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current);
  }, [spinning, isMultiplayer, reportSpinStart]);

  const handleSpinComplete = useCallback((number) => {
    setSpinning(false);
    setWinningNumber(number);
    setShowResult(true);
    setHistory((prev) => [number, ...prev].slice(0, 30));

    // Report result to multiplayer API
    if (isMultiplayer) {
      reportSpin(number);
    }

    resultTimeoutRef.current = setTimeout(() => {
      setShowResult(false);
      // Auto-start new round in multiplayer
      if (isMultiplayer) {
        startNewRound();
      }
    }, 6000);
  }, [isMultiplayer, reportSpin, startNewRound]);

  const handleStatsTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 400) {
      setShowSettings(prev => !prev);
    }
    lastTapRef.current = now;
  }, []);

  // Sync ruleLevel FROM server when connecting to a room
  useEffect(() => {
    if (mpState?.ruleLevel) setRuleLevel(mpState.ruleLevel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpState?.code]);

  // Sync ruleLevel TO server whenever dealer changes it
  useEffect(() => {
    if (!room) return;
    updateSettings({ ruleLevel });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleLevel, room?.code]);

  // Sync spin history from server in multiplayer
  useEffect(() => {
    if (mpState?.spinHistory?.length) {
      setHistory(mpState.spinHistory.map(s => s.number).reverse());
    }
  }, [mpState?.spinHistory]);

  const handleResendQR = useCallback((playerId) => {
    if (!mpState || !room) return;
    const player = mpState.players.find(p => p.id === playerId);
    const details = mpState.playerDetails?.[playerId];
    if (!player || !details?.playerToken) return;
    const sessionUrl = `https://roulette-player.rushelwedsivani.com/?room=${room.code}&pid=${playerId}&ptoken=${details.playerToken}&name=${encodeURIComponent(player.name)}`;
    setResendQRPlayer({ name: player.name, sessionUrl, pin: details.pin });
  }, [mpState, room]);

  const handleCheckoutRequest = useCallback((playerId) => {
    if (!mpState) return;
    const player = mpState.players.find(p => p.id === playerId);
    const details = mpState.playerDetails?.[playerId];
    if (!player || !details) return;
    setCheckoutPlayer({
      id: playerId,
      name: player.name,
      finalBalance: details.balance,
      initialBalance: details.initialBalance,
      profit: details.balance - details.initialBalance,
    });
  }, [mpState]);

  const handleCheckoutConfirm = useCallback(async () => {
    if (!checkoutPlayer) return;
    setCheckoutLoading(true);
    const result = await checkout(checkoutPlayer.id);
    setCheckoutLoading(false);
    if (result?.error) {
      alert('Fehler: ' + result.error);
      return;
    }
    setCheckoutPlayer(null);
  }, [checkoutPlayer, checkout]);

  const handleKickPlayer = useCallback(async (playerId, name) => {
    if (!window.confirm(`${name} wirklich entfernen?`)) return;
    await kickPlayer(playerId);
  }, [kickPlayer]);

  // Auto-exit multiplayer when room is closed externally (e.g. by admin)
  useEffect(() => {
    if (mpState?.phase === 'closed') {
      const t = setTimeout(() => closeRoom(), 3000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpState?.phase]);

  const handleReset = useCallback(() => {
    setHistory([]);
    setWinningNumber(null);
    setShowResult(false);
  }, []);

  useEffect(() => {
    if (selectedBet) {
      const timer = setTimeout(() => setSelectedBet(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [selectedBet]);

  const pct = (v) => (v * 100).toFixed(1) + '%';

  return (
    <div className="app">
      <div className="bg-felt" />

      {/* Floating top-left: title + min bet */}
      <div className="floating-info">
        <h1 className="title">ROULETTE</h1>
        <div className="min-bet-area" onClick={handleStatsTap}>
          <span className="min-bet-label">MIN. EINSATZ</span>
          <div
            className="min-bet-chip"
            style={{
              background: (CHIP_STYLES[minBet] || CHIP_STYLES[5]).bg,
              borderColor: (CHIP_STYLES[minBet] || CHIP_STYLES[5]).border,
              color: (CHIP_STYLES[minBet] || CHIP_STYLES[5]).color,
            }}
          >
            {minBet}
          </div>
        </div>
      </div>

      <main className="main">
        {/* Wheel pushed left, fills height */}
        <div className="wheel-area">
          <RouletteWheel
            targetNumber={targetNumber}
            spinning={spinning}
            onSpinComplete={handleSpinComplete}
            spinParams={currentSpinParams}
          />
          {/* Winning number overlay on wheel center */}
          <div className={`result-overlay ${showResult ? 'show' : ''}`}>
            {winningNumber != null && (
              <div className={`result-number ${getNumberColor(winningNumber)}`}>
                <span className="result-value">{winningNumber}</span>
                <span className="result-label">
                  {winningNumber === 0 ? 'ZERO' : getNumberColor(winningNumber).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          {/* === BUTTON LOGIC === */}
          {/* Solo mode: always show SPIN when not spinning */}
          {!isMultiplayer && !spinning && (
            <button className="spin-btn" onClick={handleSpin}>
              <span className="spin-btn-inner">
                <span className="spin-btn-text">SPIN</span>
              </span>
            </button>
          )}
          {/* Multiplayer: SPIEL STARTEN (waiting phase) */}
          {isMultiplayer && !spinning && mpState?.phase === 'waiting' && (
            <button className="spin-btn new-round-btn" onClick={() => startNewRound()}>
              <span className="spin-btn-inner">
                <span className="spin-btn-text">SPIEL STARTEN</span>
              </span>
            </button>
          )}
          {/* Multiplayer: WETTEN SPERREN (betting phase) */}
          {isMultiplayer && !spinning && mpState?.phase === 'betting' && (
            <button className="spin-btn close-bets-btn" onClick={() => closeBets()}>
              <span className="spin-btn-inner">
                <span className="spin-btn-text">WETTEN SPERREN</span>
              </span>
            </button>
          )}
          {/* Multiplayer: SPIN (spinning phase = bets locked) */}
          {isMultiplayer && !spinning && !showResult && mpState?.phase === 'spinning' && (
            <button className="spin-btn" onClick={handleSpin}>
              <span className="spin-btn-inner">
                <span className="spin-btn-text">SPIN</span>
              </span>
            </button>
          )}
        </div>

        {/* Right panel - History on top, Stats on bottom */}
        <div className="right-panel">
          {/* Last numbers */}
          <div className="history-section">
            <h3 className="section-title">LETZTE ZAHLEN</h3>
            <div className="history-grid">
              {history.length === 0 && (
                <div className="history-empty">Noch keine Drehung</div>
              )}
              {history.slice(0, 12).map((num, idx) => (
                <div
                  key={`${idx}-${num}`}
                  className={`history-chip ${getNumberColor(num)}`}
                >
                  {num}
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="panel-divider" />

          {/* Player list (multiplayer) */}
          {isMultiplayer && (
            <PlayerList
            state={mpState}
            onShowQR={() => setShowQR(true)}
            onCheckin={() => setShowCheckin(true)}
            onCheckout={handleCheckoutRequest}
            onResendQR={handleResendQR}
          />
          )}

          {/* Divider */}
          {isMultiplayer && <div className="panel-divider" />}

          {/* Statistics */}
          <div className="stats-section">
            <h3 className="section-title" onClick={handleStatsTap}>STATISTIK</h3>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Wette</th>
                  <th>Quote</th>
                  {stats && <th>Real</th>}
                </tr>
              </thead>
              <tbody>
                {BET_ROWS
                  .filter(row => LEVEL_VALUE[row.level] <= LEVEL_VALUE[ruleLevel])
                  .map(row => {
                    const realPct = stats && stats[row.key] != null
                      ? (stats[row.key] / stats.total)
                      : null;
                    return (
                      <tr key={row.key}>
                        <td className="bet-name-cell" onClick={() => setSelectedBet(row.key)}>
                          {row.dot && <span className={`stats-dot ${row.dot}`} />}
                          {row.label}
                        </td>
                        <td>{ODDS[row.key].payout}</td>
                        {stats && (
                          <td className="stats-pct-cell">
                            {realPct != null && (
                              <span
                                className="stats-pct-bar"
                                style={{ width: `${Math.min(realPct * 100, 100)}%` }}
                              />
                            )}
                            <span className="stats-pct-value">
                              {realPct != null ? pct(realPct) : '-'}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Settings overlay - accessible via double-tap on MIN. EINSATZ */}
      {showSettings && (
        <div className="settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-panel" onClick={e => e.stopPropagation()}>
            <h3 className="settings-panel-title">EINSTELLUNGEN</h3>

            <div className="settings-group">
              <label className="settings-label">Spielregeln</label>
              <div className="settings-buttons">
                {[
                  { value: 'simple', label: 'Einfach' },
                  { value: 'medium', label: 'Mittel' },
                  { value: 'expert', label: 'Experte' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    className={`settings-btn ${ruleLevel === opt.value ? 'active' : ''}`}
                    onClick={() => setRuleLevel(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-group">
              <label className="settings-label">Minimaler Einsatz</label>
              <div className="settings-chip-options">
                {[1, 5, 10, 25, 100].map(amount => (
                  <button
                    key={amount}
                    className={`settings-chip-btn ${minBet === amount ? 'active' : ''}`}
                    style={{
                      background: CHIP_STYLES[amount].bg,
                      borderColor: minBet === amount ? '#d4a535' : CHIP_STYLES[amount].border,
                      color: CHIP_STYLES[amount].color,
                    }}
                    onClick={() => setMinBet(amount)}
                  >
                    {amount}
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-group">
              <label className="settings-label">Wett-Timer</label>
              <div className="settings-buttons">
                {[
                  { value: 0, label: 'Aus' },
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' },
                  { value: 45, label: '45s' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    className={`settings-btn ${(mpState?.bettingTimer ?? 0) === opt.value ? 'active' : ''}`}
                    onClick={() => isMultiplayer && updateSettings({ bettingTimer: opt.value })}
                    disabled={!isMultiplayer}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-group">
              <label className="settings-label">Multiplayer</label>
              {!isMultiplayer ? (
                <button
                  className="settings-close"
                  onClick={() => {
                    createRoom({ startBalance: 500 }).then(data => {
                      if (data && !data.error) {
                        setShowSettings(false);
                      } else {
                        alert('Fehler: ' + (data?.error || 'Verbindung fehlgeschlagen'));
                      }
                    });
                  }}
                >
                  MULTIPLAYER STARTEN
                </button>
              ) : (
                <div className="mp-active-info">
                  <span className="mp-code">Raum: {room.code}</span>
                  <button className="settings-reset" onClick={() => { setShowQR(true); setShowSettings(false); }}>
                    QR ZEIGEN
                  </button>
                  <button className="settings-reset" onClick={() => { closeRoom(); }}>
                    BEENDEN
                  </button>
                </div>
              )}
            </div>

            <div className="settings-group">
              <label className="settings-label">Odds & Statistics</label>
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Bet</th>
                    <th>Payout</th>
                    {stats && <th>Actual</th>}
                  </tr>
                </thead>
                <tbody>
                  {BET_ROWS
                    .filter(row => LEVEL_VALUE[row.level] <= LEVEL_VALUE[ruleLevel])
                    .map(row => (
                      <tr key={row.key}>
                        <td className="bet-name-cell" onClick={() => setSelectedBet(row.key)}>
                          {row.dot && <span className={`stats-dot ${row.dot}`} />}
                          {row.label}
                        </td>
                        <td>{ODDS[row.key].payout}</td>
                        {stats && (
                          <td>{stats[row.key] != null ? pct(stats[row.key] / stats.total) : '-'}</td>
                        )}
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>

            <div className="settings-actions">
              <button className="settings-close" onClick={() => setShowSettings(false)}>
                FERTIG
              </button>
              <button className="settings-reset" onClick={() => { handleReset(); setShowSettings(false); }}>
                RESET
              </button>
              <button className="settings-admin-btn" onClick={() => { setShowSettings(false); window.open('/admin', '_blank'); }}>
                🎰 ADMIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code overlay */}
      {showQR && playerUrl && (
        <QROverlay url={playerUrl} code={room.code} onClose={() => setShowQR(false)} />
      )}

      {/* Check-in modal */}
      {showCheckin && (
        <CheckinModal
          onCheckin={checkin}
          onClose={() => setShowCheckin(false)}
        />
      )}

      {/* Session QR recovery modal */}
      {resendQRPlayer && (
        <ResendQRModal
          name={resendQRPlayer.name}
          sessionUrl={resendQRPlayer.sessionUrl}
          pin={resendQRPlayer.pin}
          onClose={() => setResendQRPlayer(null)}
        />
      )}

      {/* Checkout modal */}
      {checkoutPlayer && (
        <CheckoutModal
          player={checkoutPlayer}
          loading={checkoutLoading}
          onConfirm={handleCheckoutConfirm}
          onClose={() => setCheckoutPlayer(null)}
        />
      )}

      {/* Multiplayer phase indicator */}
      {isMultiplayer && (
        <div className="mp-phase-bar">
          {mpState && <>
            <span className="mp-phase-dot" data-phase={mpState.phase} />
            <span className="mp-phase-text">
              {mpState.phase === 'waiting' && 'Warte auf Spieler...'}
              {mpState.phase === 'betting' && `Runde ${mpState.round} — Wetten offen`}
              {mpState.phase === 'spinning' && 'Wetten gesperrt — bereit zum Drehen'}
              {mpState.phase === 'result' && 'Ergebnis'}
            </span>
            {timerSeconds != null && mpState.phase === 'betting' && (
              <span className={`mp-timer ${timerSeconds <= 5 ? 'urgent' : ''}`}>
                {timerSeconds}s
              </span>
            )}
            <span className="mp-player-count">{mpState.playerCount} Spieler</span>
          </>}
          {!mpState && <span className="mp-phase-text">Verbinden...</span>}
        </div>
      )}

      {/* Offline indicator — only shown when disconnected */}
      {isMultiplayer && connectionStatus !== 'connected' && (
        <div className="offline-indicator">
          <span className="offline-dot" />
          {connectionStatus === 'reconnecting' ? 'OFFLINE' : 'VERBINDEN...'}
        </div>
      )}

      {/* Notification toast feed */}
      {notifications.length > 0 && (
        <div className="notification-feed">
          {notifications.map(n => (
            <div key={n.id} className={`notification-toast${n.variant === 'bigwin' ? ' notification-bigwin' : ''}`}>{n.message}</div>
          ))}
        </div>
      )}

      {selectedBet && BET_EXAMPLES[selectedBet] && (
        <div className="bet-preview-overlay" onClick={() => setSelectedBet(null)}>
          <div className="bet-preview">
            <div className="bet-preview-header">
              <span className="bet-preview-name">
                {BET_ROWS.find(r => r.key === selectedBet)?.label}
              </span>
              <span className="bet-preview-payout">
                {ODDS[selectedBet].payout}
              </span>
            </div>
            <div className="bet-preview-desc">{BET_EXAMPLES[selectedBet].desc}</div>
            <div className="bet-table-grid">
              <div className={`bet-cell green ${BET_EXAMPLES[selectedBet].numbers.includes(0) ? 'highlight' : ''}`}>
                0
              </div>
              {Array.from({ length: 36 }, (_, i) => i + 1).map(num => (
                <div
                  key={num}
                  className={`bet-cell ${getNumberColor(num)} ${
                    BET_EXAMPLES[selectedBet].numbers.includes(num) ? 'highlight' : ''
                  }`}
                >
                  {num}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
